import { useEffect, useState } from "react";
import { Table, Button, Modal, Form, Input, Select, TimePicker, Switch, Tag, Popconfirm, Card, Row, Col, Tooltip } from "antd";
import { Plus, Edit2, Trash2, Send, Clock, Settings, Mail, Users } from "lucide-react";
import dayjs from "dayjs";

import useAutomaticEmails from "../hooks/useAutomaticEmails";

const REPORT_TYPES = [
  { value: "DAILY_CASH_REPORT", label: "Reporte de Caja Diario" },
  { value: "BUS_PROFIT_REPORT", label: "Reporte de Ganancia de Micros" },
  { value: "PENDING_DEBTS_REPORT", label: "Reporte de Deudas Pendientes" },
];

function AutomaticEmailConfig() {
  const {
    configs,
    recipients,
    loading,
    getConfigs,
    getRecipients,
    createConfig,
    updateConfig,
    deleteConfig,
    sendNow,
  } = useAutomaticEmails();

  const [form] = Form.useForm();
  const [modalOpen, setModalOpen] = useState(false);
  const [editingConfig, setEditingConfig] = useState(null);

  useEffect(() => {
    getConfigs();
    getRecipients();
  }, []);

  const handleCreate = () => {
    setEditingConfig(null);
    form.resetFields();
    form.setFieldsValue({ isActive: true });
    setModalOpen(true);
  };

  const handleEdit = (record) => {
    setEditingConfig(record);
    form.setFieldsValue({
      name: record.name,
      reportType: record.reportType,
      sendTime: record.sendTime ? dayjs(record.sendTime, "HH:mm:ss") : null,
      isActive: record.isActive,
      recipients: record.recipientsDetail?.map(r => r.id) || [],
    });
    setModalOpen(true);
  };

  const handleSubmit = async (values) => {
    const data = {
      name: values.name,
      report_type: values.reportType,
      send_time: values.sendTime.format("HH:mm:ss"),
      is_active: values.isActive ?? true,
      recipients: values.recipients,
    };

    if (editingConfig) {
      await updateConfig(editingConfig.id, data);
    } else {
      await createConfig(data);
    }
    setModalOpen(false);
    getConfigs();
  };

  const handleDelete = async (id) => {
    await deleteConfig(id);
    getConfigs();
  };

  const handleSendNow = async (id) => {
    await sendNow(id);
  };

  const columns = [
    {
      title: "Nombre",
      dataIndex: "name",
      key: "name",
      render: (text) => <span className="font-medium">{text}</span>,
    },
    {
      title: "Tipo de Reporte",
      dataIndex: "reportTypeDisplay",
      key: "reportTypeDisplay",
      render: (text) => <Tag color="blue">{text}</Tag>,
    },
    {
      title: "Hora de Envío",
      dataIndex: "sendTime",
      key: "sendTime",
      width: 120,
      render: (time) => (
        <span className="flex items-center gap-1 font-mono">
          <Clock size={14} className="text-gray-400" />
          {time?.substring(0, 5)}
        </span>
      ),
    },
    {
      title: "Destinatarios",
      dataIndex: "recipientsDetail",
      key: "recipients",
      render: (recipientsDetail) => (
        <Tooltip title={recipientsDetail?.map(r => r.name).join(", ")}>
          <span className="flex items-center gap-1">
            <Users size={14} className="text-gray-400" />
            {recipientsDetail?.length || 0} usuarios
          </span>
        </Tooltip>
      ),
    },
    {
      title: "Estado",
      dataIndex: "isActive",
      key: "isActive",
      width: 100,
      render: (active) => (
        <Tag color={active ? "green" : "default"}>
          {active ? "Activo" : "Inactivo"}
        </Tag>
      ),
    },
    {
      title: "Último Envío",
      dataIndex: "lastSentAt",
      key: "lastSentAt",
      width: 150,
      render: (date) => date ? dayjs(date).format("DD/MM/YYYY HH:mm") : "-",
    },
    {
      title: "",
      key: "actions",
      width: 180,
      render: (_, record) => (
        <div className="flex gap-2">
          <Tooltip title="Enviar ahora">
            <Button
              size="small"
              icon={<Send size={14} />}
              onClick={() => handleSendNow(record.id)}
            />
          </Tooltip>
          <Tooltip title="Editar">
            <Button
              size="small"
              icon={<Edit2 size={14} />}
              onClick={() => handleEdit(record)}
            />
          </Tooltip>
          <Popconfirm
            title="¿Eliminar configuración?"
            description="Esta acción no se puede deshacer."
            onConfirm={() => handleDelete(record.id)}
            okText="Sí, eliminar"
            cancelText="Cancelar"
          >
            <Tooltip title="Eliminar">
              <Button size="small" danger icon={<Trash2 size={14} />} />
            </Tooltip>
          </Popconfirm>
        </div>
      ),
    },
  ];

  return (
    <div>
      {/* Estadísticas */}
      <Row gutter={[16, 16]} className="mb-4">
        <Col xs={24} sm={8}>
          <Card size="small">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Settings size={20} className="text-blue-600" />
              </div>
              <div>
                <div className="text-gray-500 text-sm">Configuraciones</div>
                <div className="text-xl font-bold">{configs.length}</div>
              </div>
            </div>
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card size="small">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <Mail size={20} className="text-green-600" />
              </div>
              <div>
                <div className="text-gray-500 text-sm">Activas</div>
                <div className="text-xl font-bold text-green-600">
                  {configs.filter(c => c.isActive).length}
                </div>
              </div>
            </div>
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card size="small">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-100 rounded-lg">
                <Users size={20} className="text-purple-600" />
              </div>
              <div>
                <div className="text-gray-500 text-sm">Destinatarios Disponibles</div>
                <div className="text-xl font-bold">{recipients.length}</div>
              </div>
            </div>
          </Card>
        </Col>
      </Row>

      {/* Tabla */}
      <Card
        title={
          <div className="flex items-center gap-2">
            <Settings size={18} />
            <span>Configuraciones de Emails Automáticos</span>
          </div>
        }
        extra={
          <Button type="primary" icon={<Plus size={16} />} onClick={handleCreate}>
            Nueva Configuración
          </Button>
        }
      >
        <Table
          columns={columns}
          dataSource={configs}
          rowKey="id"
          loading={loading.get}
          pagination={false}
          size="small"
        />
      </Card>

      {/* Modal Crear/Editar */}
      <Modal
        title={editingConfig ? "Editar Configuración" : "Nueva Configuración"}
        open={modalOpen}
        onCancel={() => setModalOpen(false)}
        onOk={() => form.submit()}
        confirmLoading={loading.post || loading.put}
        okText={editingConfig ? "Guardar Cambios" : "Crear Configuración"}
        cancelText="Cancelar"
        width={550}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          initialValues={{ isActive: true }}
        >
          <Form.Item
            name="name"
            label="Nombre de la Configuración"
            rules={[{ required: true, message: "El nombre es requerido" }]}
          >
            <Input placeholder="Ej: Reporte diario para gerencia" />
          </Form.Item>

          <Form.Item
            name="reportType"
            label="Tipo de Reporte"
            rules={[{ required: true, message: "Selecciona un tipo de reporte" }]}
          >
            <Select
              options={REPORT_TYPES}
              placeholder="Seleccionar tipo de reporte"
            />
          </Form.Item>

          <Form.Item
            name="sendTime"
            label="Hora de Envío"
            rules={[{ required: true, message: "Selecciona la hora de envío" }]}
          >
            <TimePicker format="HH:mm" style={{ width: "100%" }} />
          </Form.Item>

          <Form.Item
            name="recipients"
            label="Destinatarios"
            rules={[{ required: true, message: "Selecciona al menos un destinatario" }]}
          >
            <Select
              mode="multiple"
              placeholder="Seleccionar usuarios"
              options={recipients.map((u) => ({
                value: u.id,
                label: `${u.first_name} ${u.last_name} (${u.email})`,
              }))}
              filterOption={(input, option) =>
                option.label.toLowerCase().includes(input.toLowerCase())
              }
              showSearch
            />
          </Form.Item>

          <Form.Item
            name="isActive"
            label="Estado"
            valuePropName="checked"
          >
            <Switch checkedChildren="Activo" unCheckedChildren="Inactivo" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}

export default AutomaticEmailConfig;
